import React, { useState, useEffect } from 'react';
import { useSignup } from './SignupProvider';
import PricingCalculator from '../PricingCalculator';
import { calculatePricing, PricingConfig } from '../../services/stripe';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../../services/firebase';
import { isValidTokenFormat, TokenValidationResult } from '../../models/token';

const functions = app ? getFunctions(app) : null;
const validateTokenFn = functions ? httpsCallable(functions, 'validateToken') : null;

const TableCountStep: React.FC = () => {
  const { 
    state, 
    updateTableCount, 
    updateNfcCount,
    updateTabletCount,
    updatePricing,
    setNegotiatedPricing,
    setCustomSalesToken,
    previousStep,
    goToStep
  } = useSignup();
  
  const [tableCount, setTableCount] = useState(state.data.tableCount || 1);
  const [nfcCount, setNfcCount] = useState(state.data.nfcCount || 1);
  const [tabletCount, setTabletCount] = useState(state.data.tabletCount || 1);
  const [, setCustomToken] = useState('');
  const [tokenValidation, setTokenValidation] = useState<TokenValidationResult>({ isValid: false });
  const [, setIsValidatingToken] = useState(false);

  // Validate custom sales token via Cloud Function
  const validateCustomSalesToken = async (token: string): Promise<TokenValidationResult> => {
    if (!token || !isValidTokenFormat(token)) {
      return { isValid: false, error: 'Invalid token format' };
    }

    if (!validateTokenFn) {
      return { isValid: false, error: 'Service unavailable' };
    }

    try {
      const result = await validateTokenFn({ token, tokenType: 'sales' });
      return result.data as TokenValidationResult;
    } catch (error) {
      console.error('Token validation error:', error);
      return { isValid: false, error: 'Invalid Token' };
    }
  };

  // Calculate pricing when counts change - use custom pricing if valid token
  useEffect(() => {
    let pricingConfig: PricingConfig = {
      monthlySubscription: { defaultPricePerTable: 10.00 },
      nfcHardware: { defaultPricePerNFC: 5.00 },
      tabletHardware: { defaultPricePerTablet: 495.00 }
    };

    // Apply custom pricing if valid sales token
    if (tokenValidation.isValid && tokenValidation.token?.metadata) {
      const metadata = tokenValidation.token.metadata;
      if (metadata.pricePerTable) {
        pricingConfig.monthlySubscription.negotiatedPricePerTable = metadata.pricePerTable;
      }
      if (metadata.pricePerNFC) {
        pricingConfig.nfcHardware.negotiatedPricePerNFC = metadata.pricePerNFC;
      }
      if (metadata.pricePerTablet) {
        pricingConfig.tabletHardware!.negotiatedPricePerTablet = metadata.pricePerTablet;
      }
      setNegotiatedPricing(true);
    } else {
      setNegotiatedPricing(false);
    }

    updateTableCount(tableCount);
    updateNfcCount(nfcCount);
    updateTabletCount(tabletCount);
    updatePricing(pricingConfig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableCount, nfcCount, tabletCount, tokenValidation]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleTableCountChange = (count: number) => {
    const validCount = Math.max(0, Math.min(9999, count));
    setTableCount(validCount);
    // Auto-adjust NFC count to match tables by default
    if (nfcCount === tableCount) {
      setNfcCount(validCount);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleNfcCountChange = (count: number) => {
    const validCount = Math.max(0, Math.min(9999, count));
    setNfcCount(validCount);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleTabletCountChange = (count: number) => {
    const validCount = Math.max(0, Math.min(9999, count));
    setTabletCount(validCount);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleCustomTokenChange = async (token: string) => {
    // Allow only alphanumeric characters and limit to 20 characters
    const cleanToken = token.replace(/[^a-zA-Z0-9]/g, '').slice(0, 20);
    setCustomToken(cleanToken);

    // Reset validation state
    setTokenValidation({ isValid: false });

    // Only validate if token is complete (20 characters)
    if (cleanToken.length === 20) {
      setIsValidatingToken(true);
      try {
        const validation = await validateCustomSalesToken(cleanToken);
        setTokenValidation(validation);
        // Update the signup state with the token if valid
        if (validation.isValid) {
          setCustomSalesToken(cleanToken);
        }
      } catch (error) {
        setTokenValidation({ isValid: false, error: 'Invalid Token' });
      } finally {
        setIsValidatingToken(false);
      }
    } else {
      // Clear the token from state if not complete
      setCustomSalesToken('');
    }
  };


  const handleNext = () => {
    // Allow purchase if either tables > 0 OR nfc tags > 0 OR tablets > 0 (or any combination)
    if ((tableCount > 0 || nfcCount > 0 || tabletCount > 0) && tableCount >= 0 && nfcCount >= 0 && tabletCount >= 0) {
      goToStep('payment');
    }
  };

  const handlePrevious = () => {
    previousStep();
  };

  // Calculate current pricing for display
  const currentPricing = state.paymentDetails?.calculation || calculatePricing(
    tableCount,
    nfcCount,
    tabletCount,
    state.data.pricing
  );

  return (
    <div className="table-count-step">
      <div className="step-header">
        <h2>Service Pricing</h2>
        <p>Simple, transparent pricing for your restaurant</p>
      </div>

      <div className="setup-content">
        <div className="pricing-main">
          <PricingCalculator 
            calculation={currentPricing}
            showBreakdown={true}
            isNegotiated={tokenValidation.isValid}
          />
        </div>
      </div>

      <div className="step-actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handlePrevious}
        >
          Back: Manager Details
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleNext}
        >
          Next: Payment
        </button>
      </div>

      <style>{`
        .table-count-step {
          width: 100%;
          max-width: 1000px;
          margin: 0 auto;
          padding: 20px;
        }

        .step-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .step-header h2 {
          color: #2196F3;
          margin-bottom: 8px;
          font-size: 2rem;
          font-weight: 600;
        }

        .step-header p {
          color: #6c757d;
          font-size: 1.1rem;
        }

        .setup-content {
          display: flex;
          justify-content: center;
          align-items: flex-start;
          margin: 0 auto 32px auto;
          max-width: 600px;
        }

        .pricing-main {
          width: 100%;
          max-width: 500px;
        }

        .step-actions {
          display: flex;
          justify-content: center;
          gap: 16px;
          width: 100%;
          max-width: 568px;
          margin: 32px auto 0 auto;
        }

        .btn {
          padding: 12px 24px;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          flex: 1;
        }

        .btn-primary {
          background: #2196F3;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background: #1976D2;
          transform: translateY(-1px);
        }

        .btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-secondary {
          background: #6c757d;
          color: white;
        }

        .btn-secondary:hover {
          background: #5a6268;
          transform: translateY(-1px);
        }

        /* Mobile responsive fixes */
        @media (max-width: 768px) {
          .table-count-step {
            padding: 16px;
          }

          .step-header h2 {
            font-size: 1.5rem;
          }

          .setup-content {
            width: 100%;
          }
          
          .pricing-main {
            width: 100%;
          }
          
          .step-actions {
            max-width: 100%;
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
};

export default TableCountStep;
